import { hasCutOffCopy } from '@/lib/briefs/ad-copy-cut-off';
import {
  AD_PLATFORM_PROVIDER,
  type ConceptIntent,
  namedPaidProviders,
  paidProviderOfPlatform,
} from '@/lib/paid-plan/concept-channels';
import type { BriefConceptSummary } from '@/services/data/BriefWorkspaceService';

export interface CopyUnitLike {
  status: string;
  adPlatform: string;
  copy?: unknown;
}

export interface ConceptCopyCarrier {
  id: string;
  status: string;
  adUnits: readonly CopyUnitLike[];
  intents?: readonly ConceptIntent[];
}

/**
 * The saved copy that belongs to the channels a Concept is set to run on.
 *
 * A Concept can hold copy written for other channels (a LinkedIn draft on a Concept now set to
 * Google). That copy stays saved, but it is not this Concept's copy: every surface that counts,
 * lists or previews a Concept's copy reads it through here, so they cannot disagree about which
 * options exist. The channels are the paid channel the Concept names plus any organic channel it
 * names that has ad copy of its own (organic LinkedIn reads LinkedIn copy). A Concept with no
 * named paid channel keeps all of its copy.
 */
export function unitsForConceptChannel<T extends { adPlatform: string }>(
  units: readonly T[],
  intents: readonly ConceptIntent[] | undefined,
): T[] {
  const list = intents ?? [];
  if (namedPaidProviders(list).length === 0) return [...units];
  const channels = new Set(
    list.map((entry) => paidProviderOfPlatform(entry.platform)).filter((provider) => provider),
  );
  return units.filter((unit) => {
    const provider = AD_PLATFORM_PROVIDER[unit.adPlatform];
    return provider !== undefined && channels.has(provider);
  });
}

export interface ConceptCopyState {
  written: number;
  approved: number;
  hasWritten: boolean;
  hasApproved: boolean;
}

/**
 * Approved copy that can be used. A row approved before the cut-off check existed, whose text ends
 * where it was shortened to fit a limit, is saved but is not ready: it is left out here so the
 * rail, the chain, the door and the Concept card all stop calling it approved together.
 */
export function approvedCopyUnits<T extends CopyUnitLike>(concept: { adUnits: readonly T[] }): T[] {
  return concept.adUnits.filter((unit) => unit.status === 'approved' && !hasCutOffCopy(unit.copy));
}

export function plannableApprovedUnits<T extends CopyUnitLike>(concept: {
  adUnits: readonly T[];
}): T[] {
  const approved = approvedCopyUnits(concept);
  const hasOtherChannelUnit = concept.adUnits.some((unit) => unit.adPlatform !== 'linkedin');
  return hasOtherChannelUnit ? approved.filter((unit) => unit.adPlatform !== 'linkedin') : approved;
}

export function conceptCopyState(concept: Pick<ConceptCopyCarrier, 'adUnits'>): ConceptCopyState {
  const written = concept.adUnits.length;
  const approved = plannableApprovedUnits(concept).length;
  return { written, approved, hasWritten: written > 0, hasApproved: approved > 0 };
}

export function hasApprovedCopy(concept: Pick<ConceptCopyCarrier, 'adUnits'>): boolean {
  return conceptCopyState(concept).hasApproved;
}

/**
 * How much of the copy on the channel a Concept is set to run on is approved. The Brief's rail and
 * chain ask whether the Concept has any approved copy at all (`conceptCopyState`); a Concept card
 * and the Concept page list the options for ITS channel, so their count is over those options and
 * not over copy saved for channels the Concept is no longer set to.
 */
export function conceptChannelCopyState(
  concept: Pick<ConceptCopyCarrier, 'adUnits' | 'intents'>,
): ConceptCopyState {
  const onChannel = unitsForConceptChannel(concept.adUnits, concept.intents);
  const approved = approvedCopyUnits({ adUnits: onChannel }).length;
  return {
    written: onChannel.length,
    approved,
    hasWritten: onChannel.length > 0,
    hasApproved: approved > 0,
  };
}

export function approvedCopyOffDeliveryChannel(
  concept: Pick<ConceptCopyCarrier, 'adUnits'>,
  intents: readonly ConceptIntent[],
): boolean {
  const named = namedPaidProviders(intents);
  if (named.length === 0) return false;
  const approved = approvedCopyUnits(concept);
  if (approved.length === 0) return false;
  return !approved.some((unit) => {
    const provider = AD_PLATFORM_PROVIDER[unit.adPlatform];
    return provider !== undefined && named.includes(provider);
  });
}

export interface BriefCopyCounts {
  concepts: number;
  written: number;
  approved: number;
}

export function briefCopyCounts(
  concepts: ReadonlyArray<Pick<ConceptCopyCarrier, 'adUnits'>>,
): BriefCopyCounts {
  let written = 0;
  let approved = 0;
  for (const concept of concepts) {
    const state = conceptCopyState(concept);
    if (state.hasWritten) written += 1;
    if (state.hasApproved) approved += 1;
  }
  return { concepts: concepts.length, written, approved };
}

/**
 * What the gap between approved and total Concepts is, in words, for the Brief page's Progress rows: a
 * fraction like 1/2 reads as failure when the second Concept simply has no copy yet. Null when every
 * Concept holds approved copy, or when there are none.
 */
export function copyProgressNote(counts: BriefCopyCounts): string | null {
  if (counts.concepts === 0 || counts.approved >= counts.concepts) return null;
  const noCopy = counts.concepts - counts.written;
  const awaiting = counts.written - counts.approved;
  const parts: string[] = [];
  if (noCopy > 0) {
    parts.push(noCopy === 1 ? '1 Concept has no copy yet' : `${noCopy} Concepts have no copy yet`);
  }
  if (awaiting > 0) {
    parts.push(
      awaiting === 1
        ? '1 Concept has copy waiting for your review'
        : `${awaiting} Concepts have copy waiting for your review`,
    );
  }
  if (parts.length === 0) return null;
  const rest =
    counts.approved === 0
      ? null
      : counts.approved === 1
        ? 'the other one is approved'
        : 'the rest are approved';
  return `${parts.join('; ')}${rest ? `; ${rest}` : ''}.`;
}

export function copyCountsPhrase(counts: BriefCopyCounts): string {
  if (counts.concepts === 0) return 'None yet';
  if (counts.approved === 0) {
    return counts.written > 0 ? `${counts.written} written, none approved` : 'None written';
  }
  const base = `${counts.approved} of ${counts.concepts} approved`;
  return counts.written > counts.approved ? `${base}, ${counts.written} written` : base;
}

export const COPY_NODE_ALL_APPROVED = 'All approved';

/**
 * What the chain's Copy node says. It is never blank: a node with no state line reads as unloaded
 * beside its siblings. When every Concept is approved it says so without repeating the
 * rail's count; otherwise it keeps the part the rail does not carry (written, none approved).
 */
export function copyNodePhrase(counts: BriefCopyCounts): string {
  if (counts.concepts > 0 && counts.approved === counts.concepts) return COPY_NODE_ALL_APPROVED;
  return copyCountsPhrase(counts);
}

function isPaidConcept(concept: BriefConceptSummary): boolean {
  return concept.intents.some((intent) => intent.intent === 'paid');
}

export function conceptNeedingCopyApproval(
  concepts: readonly BriefConceptSummary[],
): BriefConceptSummary | null {
  const active = concepts.filter((concept) => concept.status !== 'archived');
  const paid = active.filter(isPaidConcept);
  const pool = paid.length > 0 ? paid : active;
  return pool.find((concept) => !hasApprovedCopy(concept)) ?? pool[0] ?? null;
}
